import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import InputWithCharacterCounter from '../../components/slides/highlighted/common/InputWithCharacterCounter';
import {type TypedTranslation} from '../../i18n';
import {REQUEST_PARAMETERS_MAX_NUMBER, SALESFORCE_PARAMS} from '../../constants';
import {COMPARISON_TYPE} from '../../hooks/useCharacterCounter';
import Button from '../../incase-domain/components/button/Button';
import ChecklistTile from '../../incase-domain/components/checkbox/ChecklistTile';
import {IPresentationMeta} from '../../incase-domain/presentation';
import {RequestParam} from '../../incase-domain/requestParams';
import {PresentationSettingsFormType} from '../../incase-domain/validators/presentation/presentationSchema';
import {MAX_REQUEST_PARAM_LENGTH, requestParamsValidator} from '../../incase-domain/validators/presentation/requestParameter';
import {setPresentationValidationErrorsAction} from '../../store/actions/presentation';
import {validationErrorType} from '../../store/actions/types/error.types';
import {AppState} from '../../store/reducer';
import {presentationsValidationErrorsSelector} from '../../store/selectors/presentation.selectors';

interface PresentationRequestParameterFormProps {
    useForm: UseFormReturn<PresentationSettingsFormType>;
}

export default function PresentationRequestParameterForm({useForm}: PresentationRequestParameterFormProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {watch} = useForm;
    const presentation = watch('presentation');

    const validationErrors = useSelector((state: AppState) => presentationsValidationErrorsSelector<validationErrorType>(state));

    const [addingParameterRequired, setAddingParameterRequired] = useState(false);
    const [addingParameterOptional, setAddingParameterOptional] = useState(false);
    const [newParameterRequired, setNewParameterRequired] = useState('');
    const [newParameterOptional, setNewParameterOptional] = useState('');
    const inputParameterRequired = useRef<HTMLInputElement | null>(null);
    const inputParameterOptional = useRef<HTMLInputElement | null>(null);

    const sortedParams = useMemo(() => {
        const prioItems: RequestParam[] = [];
        const nonPrioItems = (presentation.requestParams || []).filter((item) => {
            const hasPrio = SALESFORCE_PARAMS.includes(item.key);
            if (hasPrio) {
                prioItems.push(item);
            }
            return !hasPrio;
        });

        return [...prioItems, ...nonPrioItems];
    }, [presentation.requestParams]);

    const hasSalesforceParams = useMemo(() => {
        if (!presentation?.requestParams?.length) return false;
        const presParamKeys = presentation.requestParams.map((p) => p.key);
        return SALESFORCE_PARAMS.every((key) => presParamKeys.includes(key));
    }, [presentation.requestParams]);

    const handleSwapSalesforceParams = useDebouncedCallback((): void => {
        if (!presentation._id) return;

        let updatedPresentation: IPresentationMeta;

        if (hasSalesforceParams) {
            updatedPresentation = {
                ...presentation,
                requestParams: presentation.requestParams?.filter((param) => !SALESFORCE_PARAMS.includes(param.key)),
            };
        } else {
            const salesforceParams = SALESFORCE_PARAMS.map((param) => ({key: param, required: true}));
            const restParams = (presentation.requestParams || []).filter((param) => !SALESFORCE_PARAMS.includes(param.key));

            updatedPresentation = {
                ...presentation,
                requestParams: presentation.requestParams ? [...salesforceParams, ...restParams] : salesforceParams,
            };
        }

        useForm.setValue('presentation', updatedPresentation);
    }, 150);

    useLayoutEffect(() => {
        if (addingParameterRequired) {
            inputParameterRequired.current?.focus();
        }
    }, [addingParameterRequired, presentation.requestParams?.length]);

    useLayoutEffect(() => {
        if (addingParameterOptional) {
            inputParameterOptional.current?.focus();
        }
    }, [addingParameterOptional, presentation.requestParams?.length]);

    useEffect(() => {
        return () => {
            dispatch(setPresentationValidationErrorsAction({}));
        };
    }, [dispatch]);

    const handleAddParam = (value: string, required: boolean): void => {
        const result = requestParamsValidator.safeParse({key: value, required});

        if (!result.success) {
            return;
        }

        const updatedPresentation: IPresentationMeta = {
            ...presentation,
            requestParams: [...sortedParams, {key: value, required}],
        };

        useForm.setValue('presentation', updatedPresentation);

        if (required) {
            setNewParameterRequired('');
        } else {
            setNewParameterOptional('');
        }
    };

    const handleDeleteParam = (key: string): void => {
        const updatedPresentation: IPresentationMeta = {
            ...presentation,
            requestParams: presentation.requestParams?.filter((param) => param.key !== key),
        };
        useForm.setValue('presentation', updatedPresentation);
    };

    function isSalesforceParam(param: RequestParam): boolean {
        return SALESFORCE_PARAMS.includes(param.key);
    }
    const getValidationErrors = (): JSX.Element => {
        return (
            <>
                {validationErrors?.requestParams?.map((param, index) => (
                    <div key={index} className="c-form-help is-invalid--show">
                        {Object.keys(param).map((key) => (
                            <div key={key}>
                                {Array.isArray(param[key]) &&
                                    param[key].map((item, idx) => (
                                        <div key={idx}>
                                            {item.key &&
                                                Array.isArray(item.key) &&
                                                item.key.map((innerKey, innerIdx) => {
                                                    return <div key={innerIdx}>{t(innerKey as TypedTranslation)}</div>;
                                                })}
                                        </div>
                                    ))}
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );
    };

    return (
        <>
            <div className="u-flex u-flex-col u-h-center u-v-center u-text-center u-gap-2 u-pl-4 u-pr-4 u-mb-3">
                <Button size="2xl" leading="solar:code-circle-linear" variant="ghost" padded={false} disablePointerEvents />
                <div className="c-requestParameters__title">{t('presEditPage.settings.requestParameters.title')}</div>
                <div className="c-requestParameters__info">{t('presEditPage.settings.requestParameters.info')}</div>
            </div>
            <div className="u-flex u-h-center u-mb-4">
                <ChecklistTile
                    label={t('presEditPage.settings.requestParameters.enableSalesforce')}
                    onChange={handleSwapSalesforceParams}
                    value={hasSalesforceParams}
                />
            </div>
            <div className="c-requestParameters">
                <div className="c-requestParameters__row">
                    <div className="c-requestParameters__label">{t('presEditPage.settings.requestParameters.required')}:</div>
                    {sortedParams
                        .filter((el) => el.required)
                        .map((el) => (
                            <Button
                                className="c-requestParameters__item"
                                type="button"
                                variant="solid"
                                key={el.key}
                                size="xs"
                                trailing={isSalesforceParam(el) ? '' : 'ph:x-bold'}
                                leading={isSalesforceParam(el) ? 'solar:lock-bold' : ''}
                                color={isSalesforceParam(el) ? 'info' : 'primary'}
                                disabled={isSalesforceParam(el)}
                                onClick={() => {
                                    if (!isSalesforceParam(el)) {
                                        handleDeleteParam(el.key);
                                    }
                                }}
                            >
                                {el.key}
                            </Button>
                        ))}
                    {sortedParams.length < REQUEST_PARAMETERS_MAX_NUMBER && (
                        <Button
                            className="c-requestParameters__item"
                            type="button"
                            variant="outline"
                            leading="ph:plus"
                            size="xs"
                            onClick={() => setAddingParameterRequired(true)}
                            disableHover={addingParameterRequired}
                        >
                            {addingParameterRequired ? (
                                <InputWithCharacterCounter
                                    comparisonType={COMPARISON_TYPE.GREATER_THAN}
                                    className="u-flex u-v-center"
                                    ref={inputParameterRequired}
                                    value={newParameterRequired}
                                    onChange={(value) => setNewParameterRequired(value)}
                                    enableSubmitByEnter
                                    onEnterPress={() => handleAddParam(newParameterRequired, true)}
                                    validator={(value) => value.length > 0}
                                    onBlur={() => setAddingParameterRequired(false)}
                                    maxLength={MAX_REQUEST_PARAM_LENGTH}
                                    inlineMaxLength
                                />
                            ) : (
                                t('presEditPage.settings.requestParameters.add')
                            )}
                        </Button>
                    )}
                </div>
                <div className="c-requestParameters__row">
                    <div className="c-requestParameters__label">{t('presEditPage.settings.requestParameters.optional')}:</div>
                    {sortedParams
                        .filter((el) => !el.required)
                        .map((el) => (
                            <Button
                                className="c-requestParameters__item"
                                type="button"
                                variant="soft"
                                key={el.key}
                                size="xs"
                                trailing="ph:x-bold"
                                onClick={() => {
                                    if (!isSalesforceParam(el)) {
                                        handleDeleteParam(el.key);
                                    }
                                }}
                            >
                                {el.key}
                            </Button>
                        ))}
                    {sortedParams.length < REQUEST_PARAMETERS_MAX_NUMBER && (
                        <Button
                            className="c-requestParameters__item"
                            type="button"
                            variant="outline"
                            leading="ph:plus"
                            size="xs"
                            onClick={() => setAddingParameterOptional(true)}
                            disableHover={addingParameterOptional}
                        >
                            {addingParameterOptional ? (
                                <InputWithCharacterCounter
                                    comparisonType={COMPARISON_TYPE.GREATER_THAN}
                                    className="u-flex u-v-center"
                                    ref={inputParameterOptional}
                                    value={newParameterOptional}
                                    onChange={(value) => setNewParameterOptional(value)}
                                    enableSubmitByEnter
                                    onEnterPress={() => handleAddParam(newParameterOptional, false)}
                                    onBlur={() => setAddingParameterOptional(false)}
                                    maxLength={MAX_REQUEST_PARAM_LENGTH}
                                    inlineMaxLength
                                />
                            ) : (
                                t('presEditPage.settings.requestParameters.add')
                            )}
                        </Button>
                    )}
                </div>
            </div>
            {getValidationErrors()}
        </>
    );
}
